/**
 * Where an envelope is. Terminal: completed, declined, voided.
 * "signed": the signer has finished; the company countersigns before it's completed (owner, 2026-10-25).
 */
export type EnvelopeStatus = "draft" | "sent" | "signed" | "completed" | "declined" | "voided" | "expired" | "paper";

const NEXT: Record<EnvelopeStatus, EnvelopeStatus[]> = {
  draft: ["sent", "voided"],
  sent: ["signed", "completed", "declined", "voided", "expired", "paper"],
  signed: ["completed", "voided"],
  // Back to electronic signing, or the signed pages arrived.
  paper: ["sent", "completed", "voided"],
  // A resend renews the link.
  expired: ["sent", "voided"],
  completed: [],
  declined: [],
  voided: [],
};

export function canMove(from: EnvelopeStatus, to: EnvelopeStatus): boolean {
  return NEXT[from].includes(to);
}

export class StateError extends Error {
  constructor(public readonly from: string, public readonly to: string) {
    super(`Can't move an envelope from ${from} to ${to}`);
  }
}

export function assertMove(from: EnvelopeStatus, to: EnvelopeStatus): void {
  if (!canMove(from, to)) throw new StateError(from, to);
}

export const isOpen = (s: EnvelopeStatus) => s === "sent";

/** What the signer has done so far — recorded by the server, never trusted from the browser. */
export interface SignerProgress {
  codeVerifiedAt?: string;
  consentedAt?: string;
  /** Letter documents accepted (by document id). */
  accepted: Record<string, string>;
  /** Form documents: which pack was chosen (W-8BEN vs W-9). */
  formChosen?: string;
  /** Form documents whose answers pass every rule. */
  formsComplete: Record<string, boolean>;
  reviewedAt?: string;
  signatureAdoptedAt?: string;
  /** Signed documents (by id) and when. */
  signed: Record<string, string>;
  /** Upload documents (an ID proof) with at least one picture, and when the last one came. Absent on older envelopes. */
  uploaded?: Record<string, string>;
}

export interface DocumentRef {
  id: string;
  /** "upload": pictures the signer adds (an ID proof) — sealed with the rest, never signed. */
  kind: "letter" | "form" | "upload";
}

export type SignerStep =
  | { step: "verify" }
  | { step: "consent" }
  | { step: "read"; documentId: string }
  | { step: "chooseForm"; documentId: string }
  | { step: "form"; documentId: string }
  | { step: "upload"; documentId: string }
  | { step: "review" }
  | { step: "sign"; documentId: string }
  | { step: "done" };

/**
 * The one place that decides what the signer may do next. The browser asks; the server
 * refuses any action that isn't this step (or an earlier one being revisited before signing).
 */
export function nextStep(p: SignerProgress, docs: DocumentRef[]): SignerStep {
  if (!p.codeVerifiedAt) return { step: "verify" };
  if (!p.consentedAt) return { step: "consent" };
  for (const d of docs) {
    if (d.kind === "letter" && !p.accepted[d.id]) return { step: "read", documentId: d.id };
    if (d.kind === "form" && !p.formChosen) return { step: "chooseForm", documentId: d.id };
    if (d.kind === "form" && !p.formsComplete[d.id]) return { step: "form", documentId: d.id };
    if (d.kind === "upload" && !p.uploaded?.[d.id]) return { step: "upload", documentId: d.id };
  }
  if (!p.reviewedAt) return { step: "review" };
  // Letters first, then forms: the form's certification is the last thing signed.
  const order = [...docs.filter((d) => d.kind === "letter"), ...docs.filter((d) => d.kind === "form")];
  const unsigned = order.find((d) => !p.signed[d.id]);
  return unsigned ? { step: "sign", documentId: unsigned.id } : { step: "done" };
}

/** Pictures can change until the first signature: what was reviewed is what gets sealed with what's signed. */
export function canEditUploads(p: SignerProgress): boolean {
  return Object.keys(p.signed).length === 0;
}

/** Answers can change until that form is signed; after that a change would need a new signature. */
export function canEditForm(p: SignerProgress, documentId: string): boolean {
  return !p.signed[documentId];
}

export function emptyProgress(): SignerProgress {
  return { accepted: {}, formsComplete: {}, signed: {}, uploaded: {} };
}
